import React, { useCallback, useRef, useState, useEffect } from "react";
import * as Scrivito from "scrivito";
import { Form } from "reactstrap";
import { getHomepage, getPageLink } from "utils";
import { UserNameInput } from "components/AuthInputs/UserNameInput";
import { PasswordInput } from "components/AuthInputs/PasswordInput";
import { Button, Icon } from "components";
import { useAuthStore } from "stores/authStore";
import { useAppBaseContext } from "providers";
import { useToastCleanup, useTranslate } from "hooks";

const LoginView = Scrivito.connect(({ page }) => {
  const translate = useTranslate();
  const { isSameProj } = useAppBaseContext();
  const currentUser = useAuthStore.use.currentUser();
  const { redirectToWelcomePage } = useAuthStore.use.actions();

  useEffect(() => {
    if (currentUser?.id) {
      redirectToWelcomePage();
    }
  }, [currentUser?.id, redirectToWelcomePage]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const toastRef = useRef(null);
  const { login } = useAuthStore.use.actions();
  const isUserLoginPending = useAuthStore.use.isLoading();
  const error = useAuthStore.use.error();

  const homepage = getHomepage();

  useToastCleanup(toastRef);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        await login(email, password);
      } catch (err) {
        console.error(err);
      }
    },
    [login, email, password],
  );

  if (!homepage) {
    return null;
  }

  return (
    <>
      {!isSameProj && (
        <div className="bg-white mx-auto col-md-9 col-xl-6 p-4 mb-7 font-md text-center">
          <h4 id="beta" className="text-blue-300 mx-auto">
            BETA
          </h4>
          {translate("TROX_BETA_INFO_TEXT")}
        </div>
      )}
      <Form
        className="bg-white col-md-9 col-xl-6 mx-auto p-0 d-flex flex-column font-md"
        data-testid="login-view"
      >
        <div className="p-4">
          <Scrivito.LinkTag
            data-testid="LoginView_back-home-page-link"
            to={getPageLink(homepage)}
            className="text-secondary"
          >
            <Icon
              prefix="trox"
              iconName="chevron-left-regular"
              className="me-1"
            />
            <span>{translate("LOGIN_VIEW_BACK_TO_TROX_HOMEPAGE")}</span>
          </Scrivito.LinkTag>
        </div>
        <div className="px-4 mx-auto text-center">
          <Icon
            prefix="trox"
            iconName="user-light"
            className="icon-96 text-blue-800"
          />
          <h6 className="mb-5_5">{translate("LOGIN_VIEW_LOGIN_HEADER")}</h6>
          <UserNameInput
            value={email}
            label={translate("LOGIN_VIEW_EMAIL_FIELD_LABEL")}
            error={error}
            name="userEmailField"
            id="userEmailFieldId"
            type="email"
            testId="email"
            onChange={handleEmailChange}
          />
          <PasswordInput
            classNames="mb-2"
            value={password}
            error={error}
            name="passwordField"
            id="passwordFieldId"
            testId="password"
            onChange={handlePasswordChange}
            label={translate("LOGIN_VIEW_PASSWORD_FIELD_LABEL")}
          />
          <div className="row m-0">
            {error && (
              <div className="col-md-7 p-0 text-start text-danger">
                {translate("LOGIN_VIEW_ERROR_OCCURRED")}
              </div>
            )}
            <div className={`col-md-${error ? 5 : 16} text-end p-0`}>
              <Scrivito.LinkTag
                data-testid="LoginView_reset-pass-link"
                to={page}
                params={{ view: "forgotPass" }}
              >
                {translate("LOGIN_VIEW_LOGIN_RESET")}
              </Scrivito.LinkTag>
            </div>
          </div>
          <div className="text-end mt-5 mb-6 d-flex gap-4 justify-content-end align-items-center">
            <Button
              data-testid="login-view_new-account-btn"
              color="secondary"
              onClick={() => {
                Scrivito.navigateTo(page, { view: "newAccount" });
              }}
              disabled={isUserLoginPending}
            >
              {translate("LOGIN_VIEW_CREATE_NEW_ACCOUNT")}
            </Button>
            <Button
              data-testid="login-view_login-btn"
              disabled={isUserLoginPending}
              color="primary"
              type="submit"
              onClick={handleLogin}
            >
              {translate("LOGIN_VIEW_LOGIN_BUTTON")}
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
});

export default LoginView;
